import React, { useState } from 'react';
import { Carousel } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export interface ImageCarouselItem {
  src: string;
  alt: string;
  /** Internal path ("/product/abc") or absolute URL. Slide is not clickable when empty */
  url?: string | null;
}

interface ImageCarouselProps {
  items: ImageCarouselItem[];
  /** Width / height of each slide. Defaults to a wide ~4:1 banner */
  aspectRatio?: string;
  autoPlaySpeed?: number;
}

const styles = {
  slide: (aspectRatio: string) => ({
    display: 'block',
    width: '100%',
    aspectRatio,
    overflow: 'hidden',
  }),
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },
  arrow: {
    width: 36,
    height: 36,
  },
  dot: (active: boolean) => ({
    width: active ? 20 : 8,
    height: 8,
    transition: 'width 0.3s ease',
  }),
};

const isExternal = (url: string) => /^https?:\/\//i.test(url);

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  items,
  aspectRatio = '4 / 1',
  autoPlaySpeed = 3000,
}) => {
  const [index, setIndex] = useState(0);

  if (!items?.length) return null;

  const total = items.length;
  const hasMultiple = total > 1;
  const activeIndex = Math.min(index, total - 1);

  const goTo = (i: number) => setIndex((i + total) % total);

  const renderSlide = (item: ImageCarouselItem, i: number) => {
    const img = (
      <img
        src={item.src}
        alt={item.alt}
        style={styles.image}
        loading={i === 0 ? 'eager' : 'lazy'}
        draggable={false}
      />
    );

    if (!item.url) {
      return <div style={styles.slide(aspectRatio)}>{img}</div>;
    }

    if (isExternal(item.url)) {
      return (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.slide(aspectRatio)}
        >
          {img}
        </a>
      );
    }

    return (
      <Link to={item.url} style={styles.slide(aspectRatio)}>
        {img}
      </Link>
    );
  };

  const arrowClass =
    'btn btn-light bg-white border-0 shadow-sm rounded-3 p-0 position-absolute top-50 translate-middle-y z-2 d-none d-md-flex align-items-center justify-content-center';

  return (
    <div className="position-relative rounded-4 overflow-hidden">
      <Carousel
        activeIndex={activeIndex}
        onSelect={(i) => setIndex(i)}
        controls={false}
        indicators={false}
        interval={hasMultiple ? autoPlaySpeed : null}
        pause="hover"
        touch={hasMultiple}
      >
        {items.map((item, i) => (
          <Carousel.Item key={i}>{renderSlide(item, i)}</Carousel.Item>
        ))}
      </Carousel>

      {hasMultiple && (
        <>

          <button
            type="button"
            aria-label="Previous slide"
            className={`${arrowClass} start-0 ms-3`}
            style={styles.arrow}
            onClick={() => goTo(activeIndex - 1)}
          >
            <FaChevronLeft size={12} />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            className={`${arrowClass} end-0 me-3`}
            style={styles.arrow}
            onClick={() => goTo(activeIndex + 1)}
          >
            <FaChevronRight size={12} />
          </button>

          <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3 d-flex gap-2 z-2">
            {items.map((_, i) => {
              const active = i === activeIndex;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={active}
                  className={`border-0 p-0 rounded-pill ${
                    active ? 'bg-primary' : 'bg-white bg-opacity-75'
                  }`}
                  style={styles.dot(active)}
                  onClick={() => goTo(i)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ImageCarousel;
